import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authMe, authLogout } from "../lib/api";

const AuthContext = createContext(null);

const AUTH_CHECK_TIMEOUT_MS = 8000;

function withTimeout(promise, ms) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error("auth_check_timeout")), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const checkAuth = useCallback(async () => {
    setAuthError(null);
    try {
      const me = await withTimeout(authMe(), AUTH_CHECK_TIMEOUT_MS);
      setUser(me);
    } catch (err) {
      // 401 is expected when nobody is signed in yet — no need to log.
      if (err?.response?.status && err.response.status !== 401) {
        console.warn("[Auth] /auth/me failed:", err);
      }
      setUser(null);
      if (err?.message === "auth_check_timeout") {
        setAuthError("timeout");
      } else if (!err?.response) {
        // Network error (no response) — likely offline or backend unreachable
        setAuthError("offline");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await authLogout();
    } catch (err) {
      console.warn("[Auth] logout API failed, clearing client session anyway:", err);
    }
    setUser(null);
    try {
      const me = await withTimeout(authMe(), AUTH_CHECK_TIMEOUT_MS);
      setUser(me);
    } catch {
      // Still null — ProtectedRoute will send them to the Landing.
    }
  };

  const refresh = async () => {
    await checkAuth();
  };

  // Nuclear option: clear every client-side auth artifact and reload. Any
  // ProtectedRoute stuck in a loading loop can recover from here.
  const resetHandshake = () => {
    try {
      // Delete every cookie this origin owns
      document.cookie.split(";").forEach((c) => {
        const name = c.split("=")[0].trim();
        if (!name) return;
        // Nuke across common paths / subdomains
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=None; Secure`;
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      });
      // Clear any Sentinel-specific local storage keys
      Object.keys(localStorage)
        .filter((k) => k.startsWith("projectivy."))
        .forEach((k) => localStorage.removeItem(k));
    } catch (err) {
      console.warn("[Auth] resetHandshake cleanup failed:", err);
    }
    window.location.assign("/");
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, authError, logout, refresh, setUser, resetHandshake }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
